import React, { CSSProperties, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getAccessToken } from 'utils/auth';
import { useKeyboardOffset } from 'hooks/useKeyboardOffset';
import { safeAreaModalBackdropPadding } from 'utils/safeArea';
import {
  claimPromoCode,
  PROMO_PROBLEM_MESSAGE,
  promoRedeemedLabel,
  verifyPromoCode
} from './promoVerify';

type Props = {
  playerGUID: string;
  transactionId: string;
  /** Deposit amount the code is verified against (minimum-deposit rules). */
  amount: number;
  onClose: () => void;
  /**
   * Called after any claim attempt that may have changed the deposit's state
   * (success, timeout, or a rejection) so the caller can refresh history.
   */
  onClaimed: () => void;
};

// 'unconfirmed' is terminal: the claim's outcome is unknown (timeout, dropped
// connection, unreadable reply or 5xx; it may still be running or have
// committed), so the only safe action is to close and let the refreshed history
// say whether the row is Redeemed or still Redeem.
// Redeem verifies the code and, if it is valid, claims it in the same action.
type Step = 'enter' | 'redeeming' | 'claimed' | 'unconfirmed';

type MessageTone = 'error' | 'success' | 'neutral';

const MESSAGE_COLORS: Record<MessageTone, string> = {
  error: '#c0392b',
  success: '#58d68d',
  neutral: '#ffffff'
};

// getAccessToken parses the session cookie and throws if it was cleared or is
// malformed; treat that as "not authenticated" so the dialog stays usable.
const readAccessToken = (): string => {
  try {
    return getAccessToken();
  } catch {
    return '';
  }
};

const RedeemPromoModal = ({ playerGUID, transactionId, amount, onClose, onClaimed }: Props) => {
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>('enter');
  const [message, setMessage] = useState('');
  const [tone, setTone] = useState<MessageTone>('error');

  const busy = step === 'redeeming';
  const isSettled = step === 'claimed' || step === 'unconfirmed';
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Move focus into the dialog on open and hand it back on close. If a claim
  // refresh replaces the opener, the caller re-homes focus (see HistoryView).
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    return () => {
      if (opener?.isConnected) opener.focus?.();
    };
  }, []);

  // While a request is pending every control is disabled; park focus on the
  // dialog itself so it cannot fall through to the page behind.
  useEffect(() => {
    if (busy) dialogRef.current?.focus();
    else if (step === 'unconfirmed') closeRef.current?.focus();
    else if (step !== 'claimed') inputRef.current?.focus();
  }, [busy, step]);

  // Pull focus back if it ever lands outside the modal dialog.
  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      const dialog = dialogRef.current;
      if (dialog && e.target instanceof Node && !dialog.contains(e.target)) {
        (dialog.querySelector<HTMLElement>('input:not([disabled]), button:not([disabled])') ?? dialog).focus();
      }
    };
    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, []);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!busy) onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    // Keep Tab navigation inside the dialog.
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'input:not([disabled]), button:not([disabled])'
    );
    if (!focusable || focusable.length === 0) {
      e.preventDefault();
      dialogRef.current?.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !dialogRef.current?.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
    setMessage('');
  };

  // The native keyboard overlays the WebView; lift the card above it.
  const keyboardOffset = useKeyboardOffset();

  const showMessage = (text: string, nextTone: MessageTone) => {
    setTone(nextTone);
    setMessage(text);
  };

  const handleRedeem = async () => {
    const jwt = readAccessToken();
    if (!code.trim()) {
      showMessage('Enter a promo code.', 'error');
      return;
    }
    if (!jwt) {
      showMessage(PROMO_PROBLEM_MESSAGE, 'error');
      return;
    }
    setStep('redeeming');
    setMessage('');

    const verdict = await verifyPromoCode({ playerGUID, promoCode: code, amount, jwt });
    if (!verdict.valid) {
      setStep('enter');
      showMessage(verdict.message, verdict.alreadyUsed ? 'neutral' : 'error');
      return;
    }

    const result = await claimPromoCode({ playerGUID, promoCode: code, transactionId, jwt });
    if (result.success) {
      setStep('claimed');
      showMessage(promoRedeemedLabel(verdict.rewards), 'success');
      onClaimed();
      return;
    }
    // An unknown outcome may still be in flight or have committed, so this
    // dialog is Close-only; the refreshed history decides the row state.
    // A readable rejection means eligibility changed since the code was checked,
    // so the player may try again.
    setStep(result.outcomeUnknown ? 'unconfirmed' : 'enter');
    showMessage(result.message, 'error');
    // Refresh on EVERY failed claim: a rejection can mean the row itself went
    // stale (24h window passed, or claimed from another session), in which
    // case History must stop offering Redeem.
    onClaimed();
  };

  // Portal to <body> so the overlay covers the whole viewport, not just the
  // History tab it was opened from.
  return createPortal(
    <div
      ref={dialogRef}
      style={
        keyboardOffset > 0
          ? { ...overlayStyle, paddingBottom: `calc(1rem + ${keyboardOffset}px)` }
          : overlayStyle
      }
      role='dialog'
      aria-modal='true'
      aria-label='Redeem promo code'
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      onClick={() => {
        if (!busy) onClose();
      }}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <span style={titleStyle}>Redeem promo code</span>
        <input
          ref={inputRef}
          type='text'
          value={code}
          onChange={(e) => handleCodeChange(e.target.value)}
          disabled={busy || isSettled}
          placeholder='Promo code'
          aria-label='Promo code'
          style={inputStyle}
        />
        {message && (
          <span style={{ ...messageStyle, color: MESSAGE_COLORS[tone] }} role='status'>
            {message}
          </span>
        )}
        <div style={buttonRowStyle}>
          <button ref={closeRef} type='button' onClick={onClose} disabled={busy} style={secondaryBtnStyle}>
            {isSettled ? 'Close' : 'Cancel'}
          </button>
          {!isSettled && (
            <button
              type='button'
              onClick={handleRedeem}
              disabled={busy || !code.trim()}
              style={{ ...primaryBtnStyle, opacity: busy || !code.trim() ? 0.5 : 1 }}>
              {busy ? 'Redeeming...' : 'Redeem'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RedeemPromoModal;

// Same backdrop treatment as the Change Avatar modal (z-index above the Cashier panel).
const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...safeAreaModalBackdropPadding('1rem'),
  background: 'rgba(0, 0, 0, 0.72)',
  backdropFilter: 'blur(6px)'
};
const cardStyle: CSSProperties = {
  width: '100%',
  maxWidth: 320,
  maxHeight: '100%',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '16px',
  background: '#1a2332',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '12px'
};
const titleStyle: CSSProperties = { fontSize: '0.85rem', fontWeight: 700, color: '#fff' };
const inputStyle: CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '0.85rem',
  padding: '10px 12px',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box'
};
const messageStyle: CSSProperties = { fontSize: '0.7rem', fontWeight: 600 };
const buttonRowStyle: CSSProperties = { display: 'flex', gap: '8px', justifyContent: 'flex-end' };
const secondaryBtnStyle: CSSProperties = {
  padding: '8px 14px',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: 'rgba(255,255,255,0.7)',
  fontSize: '0.75rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit'
};
const primaryBtnStyle: CSSProperties = {
  ...secondaryBtnStyle,
  background: 'rgba(233, 140, 38, 0.15)',
  border: '1px solid rgba(233, 140, 38, 0.4)',
  color: '#E98C26'
};
